import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, TextField, Button, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { PATH_TENANTS } from '../../routes/paths';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import apiClient from '../../config/apiClient';

const schema = yup.object().shape({
  name: yup.string().required('Tenant name is required'),
  description: yup.string().required('Description is required'),
  threshold: yup.number().required('Threshold is required'),
  orgId: yup.string().required('Org ID is required'),
  applicationId: yup
    .string()
    .required('Application ID is required')
    .test('is-uuid', 'Application ID must be a valid UUID', (value) => /^[0-9a-fA-F-]+$/.test(value || '')),
});

type FormData = {
  name: string;
  description: string;
  threshold: number;
  orgId: string;
  applicationId: string;
};

const EditTenantForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      threshold: 0,
      orgId: '',
      applicationId: '',
    },
  });

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const onSubmit = async (data: FormData) => {
    const tenantData = { ...data };

    try {
      const response = await apiClient.put(`/tenant/${id}`, tenantData);

      console.log('Tenant Edit:', response.data);

      toast.success('Tenant edited successfully!', {
        position: 'top-right',
      });

      reset();
      navigate(PATH_TENANTS.general.view);
    } catch (error) {
      toast.error('Error editing tenant. Please try again.', {
        position: 'top-right',
      });

      console.error('Error editing tenant:', error);
    }
  };

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const response = await apiClient.get(`/tenant/${id}`);
        setValue('name', response.data.name);
        setValue('description', response.data.description);
        setValue('threshold', response.data.threshold);
        setValue('orgId', response.data.application.name);
        setValue('applicationId', response.data.application.clientId);
      } catch (error) {
        console.error('Error fetching tenant:', error);
      }
    };

    fetchTenant();
  }, [id, setValue]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      sx={{ maxWidth: 400, margin: '0 auto', mt: 5 }}
    >
      <Typography variant="h4" gutterBottom>
        Edit Tenant
      </Typography>

      <TextField
        label="Tenant ID"
        fullWidth
        margin="normal"
        value={id || ''}
        disabled={true}
      />

      <TextField
        label="Tenant Name"
        fullWidth
        margin="normal"
        {...register('name')}
        error={!!errors.name}
        helperText={errors.name?.message}
      />

      <TextField
        label="Description"
        fullWidth
        margin="normal"
        {...register('description')}
        error={!!errors.description}
        helperText={errors.description?.message}
      />

      <TextField
        label="Threshold"
        fullWidth
        margin="normal"
        type="number"
        {...register('threshold')}
        error={!!errors.threshold}
        helperText={errors.threshold?.message}
      />

      <TextField
        label="Org ID"
        fullWidth
        margin="normal"
        {...register('orgId')}
        error={!!errors.orgId}
        helperText={errors.orgId?.message}
      />

      <TextField
        label="Application ID"
        fullWidth
        margin="normal"
        {...register('applicationId')}
        error={!!errors.applicationId}
        helperText={errors.applicationId?.message}
      />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        sx={{ mt: 3 }}
      >
        Edit Tenant
      </Button>
    </Box>
  );
};

export default EditTenantForm;
